import React, { useState, useEffect } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';

const QCMDashboard = () => {
    const { t } = useTranslation();
    const { currentQCM, sessionContext, attemptCount, elapsedTime } = useQCM();
    const [scoringHistory, setScoringHistory] = useState([]);

    // Simuler l'historique des scores (à remplacer par un vrai appel API)
    useEffect(() => {
        // TODO: Récupérer l'historique des scores depuis l'API
        const mockHistory = [
            { date: '2025-08-03', qcm_score: 85, similar_qcm_score: 78, conceptual_mapping_score: 92, global_score: 85 },
            { date: '2025-08-02', qcm_score: 72, similar_qcm_score: 65, conceptual_mapping_score: 88, global_score: 75 },
            { date: '2025-08-01', qcm_score: 90, similar_qcm_score: 85, conceptual_mapping_score: 95, global_score: 90 }
        ];
        setScoringHistory(mockHistory);
    }, []);

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-400';
        if (score >= 60) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getScoreBgColor = (score) => {
        if (score >= 80) return 'bg-green-900';
        if (score >= 60) return 'bg-yellow-900';
        return 'bg-red-900';
    };

    return (
        <div className="p-4 bg-gray-800 rounded-lg">
            <h4 className="text-xl font-bold text-purple-400 mb-4 text-center">
                📊 QCM Dashboard
            </h4>
            
            <div className="space-y-6">
                {/* Informations de session */}
                <div className="bg-gray-700 rounded-lg p-4">
                    <h5 className="text-lg font-bold text-blue-300 mb-3">Session Info</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-400">Exam:</span>
                            <span className="ml-2 text-white">{sessionContext.exam}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">User:</span>
                            <span className="ml-2 text-white">{sessionContext.user}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Current QCM:</span>
                            <span className="ml-2 text-white">{currentQCM?.qcm_id || 'N/A'}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Attempts:</span>
                            <span className="ml-2 text-white">{attemptCount}</span>
                        </div>
                    </div>
                </div>

                {/* Scores des 3 éléments */}
                <div className="bg-gray-700 rounded-lg p-4">
                    <h5 className="text-lg font-bold text-green-300 mb-3">Element Scores</h5>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-400">QCM</div>
                            <div className="text-sm text-gray-400">Main Question</div>
                            <div className="text-lg font-bold text-blue-300">--</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-400">Similar</div>
                            <div className="text-sm text-gray-400">Similar QCM</div>
                            <div className="text-lg font-bold text-green-300">--</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-400">Mapping</div>
                            <div className="text-sm text-gray-400">Conceptual</div>
                            <div className="text-lg font-bold text-purple-300">--</div>
                        </div>
                    </div>
                </div>

                {/* Historique des scores */}
                <div className="bg-gray-700 rounded-lg p-4">
                    <h5 className="text-lg font-bold text-yellow-300 mb-3">Score History</h5>
                    <div className="space-y-2">
                        {scoringHistory.map((score, index) => (
                            <div key={index} className="flex justify-between items-center p-2 bg-gray-600 rounded">
                                <span className="text-gray-300">{score.date}</span>
                                <div className="flex gap-2">
                                    <span className={`px-2 py-1 rounded text-xs ${getScoreColor(score.qcm_score)} ${getScoreBgColor(score.qcm_score)}`}>
                                        QCM: {score.qcm_score}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs ${getScoreColor(score.similar_qcm_score)} ${getScoreBgColor(score.similar_qcm_score)}`}>
                                        Similar: {score.similar_qcm_score}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs ${getScoreColor(score.conceptual_mapping_score)} ${getScoreBgColor(score.conceptual_mapping_score)}`}>
                                        Mapping: {score.conceptual_mapping_score}
                                    </span>
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${getScoreColor(score.global_score)} ${getScoreBgColor(score.global_score)}`}>
                                        Global: {score.global_score}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Métadonnées du QCM actuel */}
                {currentQCM?.metadata && (
                    <div className="bg-gray-700 rounded-lg p-4">
                        <h5 className="text-lg font-bold text-orange-300 mb-3">Current QCM Metadata</h5>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-400">Difficulty:</span>
                                <span className="ml-2 text-white">{currentQCM.metadata.difficulty}/10</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Category:</span>
                                <span className="ml-2 text-white">{currentQCM.metadata.category}</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Execution Time:</span>
                                <span className="ml-2 text-white">{currentQCM.metadata.execution_time} min</span>
                            </div>
                            <div>
                                <span className="text-gray-400">Elapsed Time:</span>
                                <span className="ml-2 text-white">{Math.round(elapsedTime / 60)} min</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QCMDashboard; 